import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
	useTanstackTable,
	createColumnHelper,
	EMPTY_ARRAY,
} from "../../../hooks/useTanstackTable";
import { useApi } from "../../../hooks/useApi";
import type { ImputacionResumen } from "../../../@types/Api_Extracted";
import { Button } from "../../ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../ui/tooltip";
import { DataTable } from "../../ui/datatable";
import {
	getFilteredRowModel,
	getPaginationRowModel,
	type Row,
} from "@tanstack/react-table";
import { Paginator } from "../../ui/paginator";
import { isInstantBetween, visualizarGramos } from "../../../lib/utils";

interface ListTableProps {
	filter?: {
		range?: {
			from?: Temporal.PlainDate;
			to?: Temporal.PlainDate;
		};
	};
}
export function ListTable(props: ListTableProps) {
	const navigate = useNavigate();

	const api = useApi();
	const query = api.useQuery("get", "/v1/Empresa/Imputaciones", {});

	const handleNewInput = () => {
		navigate("input/new");
	};

	const columns = useMemo(Columns, []);
	const table = useTanstackTable({
		columns,
		data: query.data ?? EMPTY_ARRAY,
		getPaginationRowModel: getPaginationRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		globalFilterFn: filterRow,
	});

	useEffect(() => {
		if (props.filter?.range) {
			const newFilter = structuredClone(table.getState().globalFilter ?? {});
			newFilter.range = props.filter.range;
			table.setGlobalFilter(newFilter);
		}
	}, [props.filter, table]);
	const tableState = table.getState();
	return (
		<>
			<DataTable
				table={table}
				noResultsMessage="Aún no hay imputaciones registradas"
				errorMessage="Error al cargar las imputaciones"
				isLoading={query.isPending}
				isError={query.isError}
			/>
			<div className="flex items-center justify-between mt-4">
				<div>
					{table.getPageCount() != 0 && (
						<Paginator
							pageCount={table.getPageCount()}
							pageIndex={tableState.pagination.pageIndex}
							shownPages={5}
							onPageChange={table.setPageIndex}
							canPrevious={table.getCanPreviousPage()}
							canNext={table.getCanNextPage()}
						/>
					)}
				</div>
				<Button variant="primary" onClick={handleNewInput}>
					Nueva imputación
				</Button>
			</div>
		</>
	);
}

function Columns() {
	const columnHelper = createColumnHelper<ImputacionResumen>();
	const formatter = new Intl.DateTimeFormat("es-ES", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	});
	const formatDate = (value: string) =>
		formatter.format(Temporal.Instant.from(value));

	return [
		columnHelper.accessor("desde", {
			header: "Desde",
			cell: (props) => formatDate(props.getValue()),
		}),
		columnHelper.accessor("hasta", {
			header: "Hasta",
			cell: (props) => formatDate(props.getValue()),
		}),
		columnHelper.accessor("lineas", {
			id: "referenciasCount",
			header: "Nº Referencias",
			cell: (props) => <TooltipReferencias referencias={props.getValue()} />,
		}),
		columnHelper.accessor((row) => getGramosReferencias(row.lineas), {
			id: "gramosReferencias",
			header: "CO2 Equivalente",
			cell: (props) => visualizarGramos(props.getValue()),
		}),
		columnHelper.display({
			id: "acciones",
			header: "Acciones",
			cell: (props) => <AccionesFila fila={props.row.original} />,
		}),
	];
}

function TooltipReferencias(props: {
	referencias: ImputacionResumen["lineas"];
}) {
	const referenciasTooltip = Array.from(props.referencias)
		.slice(0, 3)
		.map((ref) => `${ref.descripcionReferencia}`);

	if (props.referencias.length > 3) {
		referenciasTooltip.push(`y ${props.referencias.length - 3} más`);
	}

	// return <span>{props.referencias.length} referencias</span>;
	return (
		<Tooltip>
			<TooltipTrigger>{props.referencias.length} referencias</TooltipTrigger>
			<TooltipContent>
				<p>{referenciasTooltip.join(", ")}</p>
			</TooltipContent>
		</Tooltip>
	);
}
function AccionesFila(props: { fila: ImputacionResumen }) {
	const navigate = useNavigate();
	const handleEdit = () => {
		navigate("input/" + props.fila.idImputacion);
	};

	return (
		<div className="grid grid-cols-1 gap-2">
			<Button variant="secondary" onClick={handleEdit}>
				Editar
			</Button>
		</div>
	);
}

/** Reduce el listado de referencias y devuelve su suma de CO2 equivalente */
function getGramosReferencias(referencias: ImputacionResumen["lineas"]) {
	return Math.abs(
		Object.values(referencias).reduce((acc, ref) => {
			return acc + ref.conversion_equivalente_kilos * ref.cantidad * 1000;
		}, 0),
	);
}

function filterRow(
	row: Row<ImputacionResumen>,
	columnId: string,
	filterValue: ListTableProps["filter"],
) {
	if (filterValue?.range?.from || filterValue?.range?.to) {
		const { from, to } = filterValue.range;

		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const filtroDesde = from?.toZonedDateTime({
			timeZone,
			plainTime: "00:00:00",
		});
		const filtroHasta = to?.toZonedDateTime({
			timeZone,
			plainTime: "23:59:59",
		});

		const rowDesde = Temporal.Instant.from(row.original.desde);
		const rowHasta = Temporal.Instant.from(row.original.hasta);

		if (isInstantBetween(rowDesde, filtroDesde ?? null, filtroHasta ?? null))
			return true; // Show row if it falls within the date range

		if (isInstantBetween(rowHasta, filtroDesde ?? null, filtroHasta ?? null))
			return true; // Show row if it falls within the date range

		return false; // Hide row if it does not fall within the date range
	}
	return true; // If no filter is applied, return true to show the row
}
